import {Evidently, EvidentlyClientConfig} from '@aws-sdk/client-evidently';
import {fromWebToken} from '@aws-sdk/credential-providers';
import {CognitoIdentity} from '@aws-sdk/client-cognito-identity';
import {getIdentity} from '@soluto-private/mx-analytics';
import {ConfigProvider} from '../Provider';

export type EvidentlyProviderConfiguration = {
    project: string;
    entityId?: string;
    credentials: {
        identityPoolId: string;
        roleArn: string;
    };
    context?: Record<string, unknown>;
} & Omit<EvidentlyClientConfig, 'credentials'>;

export class EvidentlyProvider implements ConfigProvider {
    private readonly _client: Evidently;

    constructor(private readonly _configuration: EvidentlyProviderConfiguration) {
        const {
            project,
            entityId = getIdentity('UserBrowserId'),
            credentials: {identityPoolId, roleArn},
            region = 'us-east-1',
            context = {},
            ...rest
        } = this._configuration;
        const cognito = new CognitoIdentity({region});
        const credentials = () =>
            cognito
                .getId({IdentityPoolId: identityPoolId})
                .then(({IdentityId}) => cognito.getOpenIdToken({IdentityId}))
                .then(({Token}) => Token)
                .then(webIdentityToken => fromWebToken({roleArn, webIdentityToken, clientConfig: {region}})());
        this._client = new Evidently({region, credentials, ...rest});
        this._configuration = {project, entityId, credentials: {identityPoolId, roleArn}, region, context, ...rest};
    }

    public async get<T>(key: string, extraData: Record<string, unknown> = {}): Promise<T> {
        const {project, entityId, context} = this._configuration;
        const evaluationContext = {hostname: window.location.hostname, ...context, ...extraData} as any;
        const data = await this._client.evaluateFeature({project, entityId, feature: key, evaluationContext});
        return Object.values(data.value)[0];
    }
}
